import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Calendar, CreditCard, DollarSign, Eye, Filter, FileText, MoreHorizontal, Printer, Search, UserRound, Wallet } from 'lucide-react'
import { toast } from 'sonner'

import { financeApi } from '@/services/finance'
import { orgTreeApi } from '@/services/org-tree'
import { workersApi } from '@/services/workers'
import { SearchableSelect } from '@/components/common/searchable-select'
import { DataTablePagination } from '@/components/common/data-table-pagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useOrganizationDateLocale } from '@/lib/org-date-locale'
import { parseServerDate, useTenantTimeZone } from '@/lib/tenant-date-time'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { getUserDropdownKeywords, getUserDropdownLabel } from '@/lib/user-display'
import { cn, formatCurrency } from '@/lib/utils'

type WorkerInvoicePayment = {
  id?: string | number
  invoice_id?: string | number
  amount?: string | number | null
  payment_method?: string | null
  payment_method_label?: string | null
  paid_on?: string | null
  payment_ref?: string | null
  transaction_id?: string | null
  remarks?: string | null
}

type WorkerInvoiceRow = {
  id: string | number
  invoice_ref?: string | null
  assignment_id?: string | number | null
  worker_id?: string | number | null
  worker_code?: string | number | null
  worker_name?: string | null
  worker_user_id?: string | number | null
  worker_phone?: string | null
  worker_email?: string | null
  job_id?: string | number | null
  job_ref?: string | null
  job_title?: string | null
  period_start?: string | null
  period_end?: string | null
  jobs_count?: number
  total_earned?: string | number | null
  total_paid?: string | number | null
  pending_amount?: string | number | null
  balance?: string | number | null
  status?: 'paid' | 'partial' | 'pending' | 'overpaid' | string
  approved_by_name?: string | null
  approved_at?: string | null
  closed_on?: string | null
  last_activity?: string | null
  payments?: WorkerInvoicePayment[]
  snapshot_document?: {
    lines?: {
      work_entries?: Array<Record<string, unknown>>
      payroll_summary?: Record<string, unknown> | null
    }
    payments?: WorkerInvoicePayment[]
  } | null
}

type WorkerInvoicesResponse = {
  count?: number
  results?: WorkerInvoiceRow[]
  summary?: {
    total_payout?: string | number | null
    total_paid?: string | number | null
    total_pending?: string | number | null
    periods_count?: number
  }
}

const getInvoiceDisplayRef = (invoice: WorkerInvoiceRow) => invoice.invoice_ref || String(invoice.id).padStart(6, '0')

const PAYMENT_STATUS_OPTIONS = {
  all: 'All statuses',
  pending: 'Pending',
  partial: 'Partial',
  paid: 'Paid',
  overpaid: 'Overpaid',
} as const

const toNumber = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') {
    return 0
  }

  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const formatLabel = (value?: string | number | null) => {
  const normalized = String(value ?? '').trim().replace(/[_-]+/g, ' ')
  if (!normalized) return '-'
  return normalized.replace(/\b\w/g, (character) => character.toUpperCase())
}

const getSnapshotEntryAmount = (entry: Record<string, unknown>) => {
  const totalPay = toNumber(entry.total_pay as string | number | null)
  if (totalPay > 0) return totalPay
  return toNumber(entry.amount as string | number | null)
}

const getSnapshotEntryDescriptionHtml = (entry: Record<string, unknown>) => {
  const payType = formatLabel(entry.pay_type as string | number | null)
  const additionalDetails = Array.isArray(entry.additional_pay_details)
    ? entry.additional_pay_details
    : []
  const additionalText = additionalDetails
    .map((item) => {
      if (!item || typeof item !== 'object') return ''
      const record = item as Record<string, unknown>
      const amount = toNumber(record.amount as string | number | null)
      if (amount <= 0) return ''
      return `${formatLabel(record.label as string | number | null)} ${formatCurrency(amount)}`
    })
    .filter(Boolean)
    .join(', ')

  return additionalText
    ? `${escapeHtml(payType)}<br><span class="muted">Additional: ${escapeHtml(additionalText)}</span>`
    : escapeHtml(payType)
}

const escapeHtml = (value?: string | number | null) => {
  const source = value == null ? '' : String(value)
  return source
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const compactText = (...parts: Array<string | number | null | undefined>) =>
  parts.map((part) => String(part ?? '').trim()).filter(Boolean).join(', ')

const readString = (source: unknown, keys: string[]) => {
  if (!source || typeof source !== 'object') return ''
  const record = source as Record<string, unknown>
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return ''
}

const normalizeThemeColor = (value: string) => {
  const trimmed = value.trim()
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed
  if (/^[0-9a-fA-F]{6}$/.test(trimmed)) return `#${trimmed}`
  return '#0f172a'
}

export const buildCompanyProfile = (
  serviceInstance: unknown,
  fallbackName = 'Ellan One',
  parentServiceInstance?: unknown,
  resolvedContact?: { address?: string; email?: string },
) => {
  const config = serviceInstance && typeof serviceInstance === 'object'
    ? (serviceInstance as Record<string, unknown>).config
    : null
  const parentConfig = parentServiceInstance && typeof parentServiceInstance === 'object'
    ? (parentServiceInstance as Record<string, unknown>).config
    : null
  const configRecord = config && typeof config === 'object' ? config : null
  const parentConfigRecord = parentConfig && typeof parentConfig === 'object' ? parentConfig : null
  const source = serviceInstance && typeof serviceInstance === 'object' ? serviceInstance : {}
  const parentSource = parentServiceInstance && typeof parentServiceInstance === 'object' ? parentServiceInstance : {}
  const sourceRecord = source as Record<string, unknown>
  const parentSourceRecord = parentSource as Record<string, unknown>
  const ownerProfile = sourceRecord.owner_profile && typeof sourceRecord.owner_profile === 'object'
    ? sourceRecord.owner_profile
    : null
  const branchAdminProfile = sourceRecord.branch_admin_profile && typeof sourceRecord.branch_admin_profile === 'object'
    ? sourceRecord.branch_admin_profile
    : null
  const parentOwnerProfile = parentSourceRecord.owner_profile && typeof parentSourceRecord.owner_profile === 'object'
    ? parentSourceRecord.owner_profile
    : null
  const name = readString(configRecord, ['company_name', 'business_name', 'invoice_company_name', 'company_registration_name'])
    || readString(source, ['company_registration_name', 'company_name', 'business_name', 'invoice_company_name', 'name', 'title', 'parent_instance_name'])
    || readString(parentConfigRecord, ['company_name', 'business_name', 'invoice_company_name', 'company_registration_name'])
    || readString(parentSource, ['company_registration_name', 'company_name', 'business_name', 'invoice_company_name', 'name', 'title', 'parent_instance_name'])
    || fallbackName
  const logoUrl = readString(configRecord, ['logo_url', 'company_logo', 'invoice_logo', 'logo'])
    || readString(source, ['logo_url', 'company_logo', 'invoice_logo', 'logo', 'logo_path'])
    || readString(parentConfigRecord, ['logo_url', 'company_logo', 'invoice_logo', 'logo'])
    || readString(parentSource, ['logo_url', 'company_logo', 'invoice_logo', 'logo', 'logo_path'])
  const registrationBody = readString(parentConfigRecord, ['company_registration_body', 'registration_body', 'registered_body', 'tax_registration_body'])
    || readString(parentSource, ['company_registration_body', 'registration_body', 'registered_body', 'tax_registration_body'])
    || readString(configRecord, ['company_registration_body', 'registration_body', 'registered_body', 'tax_registration_body'])
    || readString(source, ['company_registration_body', 'registration_body', 'registered_body', 'tax_registration_body'])
  const registrationName = readString(parentConfigRecord, ['company_registration_name', 'registered_company_name', 'legal_name'])
    || readString(parentSource, ['company_registration_name', 'registered_company_name', 'legal_name'])
    || readString(configRecord, ['company_registration_name', 'registered_company_name', 'legal_name'])
    || readString(source, ['company_registration_name', 'registered_company_name', 'legal_name'])
  const registrationNumber = readString(parentConfigRecord, ['registration_number', 'company_registration_number', 'reg_no', 'tax_registration_number', 'vat_number', 'gst_number'])
    || readString(parentSource, ['registration_number', 'company_registration_number', 'reg_no', 'tax_registration_number', 'vat_number', 'gst_number', 'account_nbr'])
    || readString(configRecord, ['registration_number', 'company_registration_number', 'reg_no', 'tax_registration_number', 'vat_number', 'gst_number'])
    || readString(source, ['registration_number', 'company_registration_number', 'reg_no', 'tax_registration_number', 'vat_number', 'gst_number', 'account_nbr'])
  const registrationDetails = [
    registrationBody,
    registrationName && registrationName !== name ? registrationName : '',
    registrationNumber,
  ].filter(Boolean).join(' · ')
  const branchName = readString(source, ['name', 'title', 'branch_name', 'service_instance_name'])
  const displayBranchName = branchName && branchName !== name ? branchName : ''
  const email = resolvedContact?.email
    || readString(configRecord, ['invoice_email', 'branch_email', 'contact_email', 'email'])
    || readString(source, ['email', 'branch_email', 'admin_email', 'branch_admin_email', 'primary_role_email', 'owner_email'])
    || readString(branchAdminProfile, ['email'])
    || readString(ownerProfile, ['email'])
    || readString(parentSource, ['email', 'admin_email', 'owner_email'])
    || readString(parentOwnerProfile, ['email'])
  const phone = readString(configRecord, ['invoice_phone', 'contact_phone', 'phone', 'phn_nbr']) || readString(source, ['phone', 'phn_nbr'])
  const address = resolvedContact?.address
    || readString(configRecord, ['invoice_address', 'company_address', 'address'])
    || compactText(
      readString(source, ['address_line_1', 'location']),
      readString(source, ['address_line_2']),
      readString(source, ['city']),
      readString(source, ['postcode', 'zipcode']),
      readString(source, ['country_name']),
    )
  const primaryColor = normalizeThemeColor(
    readString(configRecord, ['primary_color', 'brand_color', 'theme_color', 'invoice_primary_color'])
      || readString(source, ['primary_color', 'brand_color', 'theme_color'])
      || '#0f172a',
  )
  return { name, branchName: displayBranchName, logoUrl, registrationBody, registrationName, registrationNumber, registrationDetails, email, phone, address, primaryColor }
}

const renderCompanyLogoHtml = (logoUrl: string, name: string) => (
  logoUrl
    ? `<img src="${escapeHtml(logoUrl)}" alt="${escapeHtml(name)} logo" />`
    : `<div class="logo-fallback">${escapeHtml(name.slice(0, 2).toUpperCase())}</div>`
)

const renderCompanySealHtml = (name: string, branchName?: string | null) => {
  const sealName = name || 'Ellan One'
  const shortName = sealName.length > 22 ? `${sealName.slice(0, 22)}...` : sealName
  const sealInitials = sealName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((part) => part[0]?.toUpperCase())
    .join('')
    || 'EO'

  return `
    <div class="company-seal" aria-label="${escapeHtml(sealName)} seal">
      <div class="seal-ring">
        <div class="seal-name">${escapeHtml(shortName)}</div>
        <div class="seal-initials">${escapeHtml(sealInitials)}</div>
        <div class="seal-branch">${escapeHtml(branchName || 'Company Seal')}</div>
      </div>
    </div>
  `
}

/** Optional `timeZone` shows instants on their tenant date; date-only values are never shifted. */
const formatDateLabel = (value?: string | null, locale = 'en-US', timeZone?: string | null) => {
  if (!value) {
    return '-'
  }

  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value)
  const parsed = isDateOnly
    ? new Date(`${value}T12:00:00`)
    : timeZone ? parseServerDate(value) : new Date(value)
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return value
  }

  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(timeZone && !isDateOnly ? { timeZone } : {}),
  }).format(parsed)
}

const getDateKey = (value?: string | null) => {
  if (!value) {
    return ''
  }

  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00`)
    : new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return `${parsed.getFullYear()}-${parsed.getMonth()}-${parsed.getDate()}`
}

const formatDateRangeLabel = (start?: string | null, end?: string | null, locale = 'en-US') => {
  if (!start && !end) {
    return '-'
  }

  if (!start || !end) {
    return formatDateLabel(start || end, locale)
  }

  if (getDateKey(start) === getDateKey(end)) {
    return formatDateLabel(start, locale)
  }

  return `${formatDateLabel(start, locale)} - ${formatDateLabel(end, locale)}`
}

/** Optional `timeZone` (tenant IANA zone) shows the instant in that zone; omitted keeps the browser zone. */
const formatDateTimeLabel = (value?: string | null, locale = 'en-US', timeZone?: string | null) => {
  if (!value) {
    return '-'
  }

  const parsed = timeZone ? parseServerDate(value) : new Date(value)
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return value
  }

  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(parsed)
}

const getInvoicePayments = (invoice: WorkerInvoiceRow) =>
  invoice.payments?.length
    ? invoice.payments
    : invoice.snapshot_document?.payments ?? []

function renderStatusBadge(status?: string | null) {
  const normalized = String(status || 'pending').toLowerCase()
  const label = PAYMENT_STATUS_OPTIONS[normalized as keyof typeof PAYMENT_STATUS_OPTIONS]
    || normalized.replace(/_/g, ' ')

  return (
    <Badge
      variant="outline"
      className={cn(
        'capitalize',
        normalized === 'paid' && 'border-emerald-200 bg-emerald-50 text-emerald-700',
        normalized === 'partial' && 'border-blue-200 bg-blue-50 text-blue-700',
        normalized === 'pending' && 'border-amber-200 bg-amber-50 text-amber-700',
        normalized === 'overpaid' && 'border-purple-200 bg-purple-50 text-purple-700',
      )}
    >
      {label}
    </Badge>
  )
}

export default function WorkerInvoicesPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const organizationDateLocale = useOrganizationDateLocale(serviceInstanceId)
  const workerInvoiceTimeZone = useTenantTimeZone(serviceInstanceId)

  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [selectedWorkerId, setSelectedWorkerId] = useState(searchParams.get('worker_id') || 'all')
  const [paymentStatus, setPaymentStatus] = useState(searchParams.get('payment_status') || 'all')
  const [dateFrom, setDateFrom] = useState(searchParams.get('date_from') || '')
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') || '')
  const [page, setPage] = useState(() => {
    const value = Number(searchParams.get('page') || '1')
    return Number.isFinite(value) && value > 0 ? value : 1
  })
  const [pageSize, setPageSize] = useState(() => {
    const value = Number(searchParams.get('page_size') || '25')
    return Number.isFinite(value) && value > 0 ? value : 25
  })
  const deferredSearch = useDeferredValue(search.trim())

  const resendInvoiceMutation = useMutation({
    mutationFn: (invoiceId: string | number) => financeApi.resendWorkerPaymentInvoice(invoiceId),
    onSuccess: () => {
      toast.success('Worker invoice resent successfully')
      queryClient.invalidateQueries({ queryKey: ['worker-invoices'] })
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to resend worker invoice.'
      toast.error(message)
    },
  })

  useEffect(() => {
    const nextSearchValue = searchParams.get('search') || ''
    const nextWorkerId = searchParams.get('worker_id') || 'all'
    const nextPaymentStatus = searchParams.get('payment_status') || 'all'
    const nextDateFrom = searchParams.get('date_from') || ''
    const nextDateTo = searchParams.get('date_to') || ''
    const nextPage = Number(searchParams.get('page') || '1')
    const nextPageSize = Number(searchParams.get('page_size') || '25')

    setSearch(nextSearchValue)
    setSelectedWorkerId(nextWorkerId)
    setPaymentStatus(nextPaymentStatus)
    setDateFrom(nextDateFrom)
    setDateTo(nextDateTo)
    setPage(Number.isFinite(nextPage) && nextPage > 0 ? nextPage : 1)
    setPageSize(Number.isFinite(nextPageSize) && nextPageSize > 0 ? nextPageSize : 25)
  }, [searchParams])

  useEffect(() => {
    const params = new URLSearchParams()

    if (serviceInstanceId) params.set('service_instance_id', serviceInstanceId)
    if (deferredSearch) params.set('search', deferredSearch)
    if (selectedWorkerId !== 'all') params.set('worker_id', selectedWorkerId)
    if (paymentStatus !== 'all') params.set('payment_status', paymentStatus)
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)
    if (page > 1) params.set('page', String(page))
    if (pageSize !== 25) params.set('page_size', String(pageSize))

    const nextSearch = params.toString()
    const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search
    if (nextSearch !== currentSearch) {
      navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true })
    }
  }, [dateFrom, dateTo, deferredSearch, location.pathname, location.search, navigate, page, pageSize, paymentStatus, selectedWorkerId, serviceInstanceId])

  const workersQuery = useQuery({
    queryKey: ['worker-invoices-workers', serviceInstanceId],
    queryFn: () => workersApi.getWorkers({ page_size: 200, active: true, service_instance_id: serviceInstanceId ?? undefined }),
    staleTime: 60_000,
  })

  const workerFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All workers', keywords: 'all workers' },
      ...(workersQuery.data?.results ?? []).map((worker) => ({
        value: String(worker.id),
        label: getUserDropdownLabel(worker, worker.id),
        keywords: getUserDropdownKeywords(worker),
      })),
    ],
    [workersQuery.data?.results],
  )

  const invoicesQuery = useQuery<WorkerInvoicesResponse>({
    queryKey: ['worker-invoices', page, pageSize, selectedWorkerId, paymentStatus, dateFrom, dateTo, deferredSearch, serviceInstanceId],
    queryFn: () => financeApi.getWorkerPaymentPeriods({
      page,
      page_size: pageSize,
      worker_id: selectedWorkerId !== 'all' ? selectedWorkerId : undefined,
      payment_status: paymentStatus !== 'all' ? paymentStatus : undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      search: deferredSearch || undefined,
      service_instance_id: serviceInstanceId ?? undefined,
    }),
    enabled: Boolean(serviceInstanceId),
  })

  const serviceInstanceQuery = useQuery({
    queryKey: ['worker-invoice-service-instance', serviceInstanceId],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
  })
  const parentServiceInstanceId = serviceInstanceQuery.data?.parent_instance_id
  const parentServiceInstanceQuery = useQuery({
    queryKey: ['worker-invoice-parent-service-instance', parentServiceInstanceId, serviceInstanceId],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(parentServiceInstanceId!),
    enabled: Boolean(parentServiceInstanceId && parentServiceInstanceId !== serviceInstanceId),
  })

  // Company address/email for the invoice PDF come from My Branch → Node Address
  // and My Branch → Credentials → Email Providers / Communication Routing
  // (purpose: Invoice / Booking / Order) — not from plain fields on the service
  // instance, which is why they were never populated from serviceInstanceQuery.
  const primaryNodeId = serviceInstanceQuery.data?.primary_node ?? parentServiceInstanceQuery.data?.primary_node
  const nodeAddressQuery = useQuery({
    queryKey: ['worker-invoice-node-address', primaryNodeId],
    queryFn: () => orgTreeApi.listAddresses(primaryNodeId!),
    enabled: Boolean(primaryNodeId),
  })
  const communicationCredentialsQuery = useQuery({
    queryKey: ['worker-invoice-communication-credentials', serviceInstanceId],
    queryFn: () => orgTreeApi.getServiceInstanceCredentials(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
    retry: false,
  })
  const resolvedCompanyAddress = nodeAddressQuery.data?.[0]?.address_for || nodeAddressQuery.data?.[0]?.name || ''
  const invoiceRoute = communicationCredentialsQuery.data?.communication?.routes?.invoice_booking_order?.email
  const resolvedCompanyEmail = invoiceRoute?.provider_key
    ? communicationCredentialsQuery.data?.communication?.providers?.[invoiceRoute.provider_key]?.host_user ?? ''
    : ''

  const rows = invoicesQuery.data?.results ?? []
  const summary = invoicesQuery.data?.summary
  const totalCount = invoicesQuery.data?.count ?? rows.length
  const companyProfile = buildCompanyProfile(serviceInstanceQuery.data, 'Ellan One', parentServiceInstanceQuery.data, {
    address: resolvedCompanyAddress,
    email: resolvedCompanyEmail,
  })

  const openWorkerInvoice = (invoice: WorkerInvoiceRow, options: { print?: boolean } = {}) => {
    const printWindow = window.open('', '_blank', 'width=1000,height=800')
    if (!printWindow) {
      return
    }

    const payments = getInvoicePayments(invoice)
    const invoiceRef = getInvoiceDisplayRef(invoice)
    const workerName = invoice.worker_name || `Worker #${invoice.worker_id ?? '-'}`
    const workerContact = compactText(invoice.worker_phone, invoice.worker_email)
    const jobLabel = invoice.job_title || invoice.job_ref || (invoice.job_id ? `Job #${invoice.job_id}` : 'Job payout')
    const invoiceDate = invoice.closed_on || invoice.last_activity || invoice.period_end
    const approvedBy = invoice.approved_by_name || 'Not captured'
    const paymentRows = payments.length
      ? payments.map((payment) => `
        <tr>
          <td>${escapeHtml(formatDateTimeLabel(payment.paid_on, organizationDateLocale, workerInvoiceTimeZone))}</td>
          <td>${escapeHtml(payment.payment_method_label || payment.payment_method || '-')}</td>
          <td>${escapeHtml(payment.payment_ref || payment.transaction_id || '-')}</td>
          <td class="right">${escapeHtml(formatCurrency(toNumber(payment.amount)))}</td>
        </tr>
      `).join('')
      : '<tr><td colspan="4" class="center muted">No payments recorded</td></tr>'
    const workEntries = Array.isArray(invoice.snapshot_document?.lines?.work_entries)
      ? invoice.snapshot_document?.lines?.work_entries ?? []
      : []
    const workRows = workEntries.length
      ? workEntries.map((entry) => `
        <tr>
          <td>${escapeHtml(String(entry.work_date ?? entry.date ?? '-'))}</td>
          <td>${escapeHtml(jobLabel)}</td>
          <td>${escapeHtml(String(entry.workday_entry_ref ?? '-'))}</td>
          <td>${escapeHtml(String(entry.hours ?? entry.total_hours ?? '-'))}</td>
          <td>${getSnapshotEntryDescriptionHtml(entry)}</td>
          <td class="right">${escapeHtml(formatCurrency(getSnapshotEntryAmount(entry)))}</td>
        </tr>
      `).join('')
      : `<tr><td>${escapeHtml(formatDateRangeLabel(invoice.period_start, invoice.period_end, organizationDateLocale))}</td><td>${escapeHtml(jobLabel)}</td><td>-</td><td>${escapeHtml(String(invoice.jobs_count ?? 1))}</td><td>Worker payout</td><td class="right">${escapeHtml(formatCurrency(toNumber(invoice.total_earned)))}</td></tr>`

    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Worker Invoice #${escapeHtml(invoiceRef)}</title>
          <style>
            @page { size: A4; margin: 14mm; }
            * { box-sizing: border-box; }
            body { margin: 0; background: #f3f4f6; color: #111827; font-family: Arial, sans-serif; }
            .page { width: 210mm; min-height: 297mm; margin: 0 auto; background: #fff; padding: 14mm; display: flex; flex-direction: column; }
            .header { display: flex; justify-content: space-between; gap: 24px; border-bottom: 5px solid ${escapeHtml(companyProfile.primaryColor)}; padding-bottom: 14px; background: #fff; }
            .brand { display: flex; gap: 12px; align-items: flex-start; }
            .brand img { width: 54px; height: 54px; object-fit: contain; }
            .logo-fallback { width: 54px; height: 54px; border-radius: 12px; background: ${escapeHtml(companyProfile.primaryColor)}; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 700; }
            h1, h2, h3, p { margin: 0; }
            h1 { color: ${escapeHtml(companyProfile.primaryColor)}; font-size: 25px; letter-spacing: 0.08em; text-transform: uppercase; }
            h2 { margin-top: 18px; font-size: 13px; text-transform: uppercase; letter-spacing: 0.08em; color: #0f172a; }
            .muted { color: #64748b; }
            .small { font-size: 11px; line-height: 1.45; }
            .meta { text-align: right; font-size: 12px; line-height: 1.5; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-top: 18px; }
            .box { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 11px; }
            th, td { border: 1px solid #e2e8f0; padding: 7px 8px; text-align: left; vertical-align: top; }
            th { background: #f8fafc; color: #334155; font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; }
            .right { text-align: right; }
            .center { text-align: center; }
            .totals { margin-left: auto; width: 45%; }
            .total-due td { background: #fffbeb; font-weight: 700; font-size: 13px; }
            .summary-row { position: relative; margin-top: 18px; min-height: 116px; }
            .company-seal { position: fixed; left: calc(14mm + (210mm - 28mm) * 0.3); bottom: 30mm; width: 64px; height: 64px; border: 1.5px solid ${escapeHtml(companyProfile.primaryColor)}; border-radius: 999px; color: ${escapeHtml(companyProfile.primaryColor)}; display: flex; align-items: center; justify-content: center; transform: translateX(-50%) rotate(-10deg); opacity: 0.72; z-index: 2; pointer-events: none; }
            .seal-ring { width: 54px; height: 54px; border: 1px dashed ${escapeHtml(companyProfile.primaryColor)}; border-radius: 999px; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 5px; }
            .seal-name { font-size: 5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; max-width: 45px; line-height: 1.1; }
            .seal-initials { margin: 3px 0; font-size: 13px; font-weight: 800; letter-spacing: 0.04em; }
            .seal-branch { font-size: 4.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; max-width: 45px; line-height: 1.1; }
            .footer { margin-top: auto; border-top: 1.5px solid ${escapeHtml(companyProfile.primaryColor)}; padding-top: 10px; display: flex; justify-content: space-between; gap: 16px; font-size: 10px; color: #64748b; }
            @media print { body { background: #fff; } .page { width: auto; min-height: auto; margin: 0; padding: 0; } }
          </style>
        </head>
        <body>
          <main class="page">
            <section class="header">
              <div class="brand">
                ${renderCompanyLogoHtml(companyProfile.logoUrl, companyProfile.name)}
                <div>
                  <h3>${escapeHtml(companyProfile.name)}</h3>
                  ${companyProfile.registrationDetails ? `<p class="small muted">${escapeHtml(companyProfile.registrationDetails)}</p>` : ''}
                  ${companyProfile.branchName ? `<p class="small muted">Branch: ${escapeHtml(companyProfile.branchName)}</p>` : ''}
                  <p class="small muted">${escapeHtml(companyProfile.address || 'Company address not set')}</p>
                  <p class="small muted">${escapeHtml(compactText(companyProfile.email, companyProfile.phone) || 'Company email not set')}</p>
                </div>
              </div>
              <div class="meta">
                <h1>Worker Invoice</h1>
                <div>Invoice No.: #${escapeHtml(invoiceRef)}</div>
                <div>Invoice Date: ${escapeHtml(formatDateLabel(invoiceDate, organizationDateLocale, workerInvoiceTimeZone))}</div>
                <div>Approved by: ${escapeHtml(approvedBy)}</div>
                <div>Payment Status: ${escapeHtml(invoice.status || 'pending')}</div>
              </div>
            </section>
            <section class="grid">
              <div class="box">
                <h3>Pay To</h3>
                <p class="small">${escapeHtml(workerName)}</p>
                <p class="small muted">Worker No: ${escapeHtml(invoice.worker_code ?? '-')}</p>
                ${workerContact ? `<p class="small muted">${escapeHtml(workerContact)}</p>` : ''}
              </div>
              <div class="box">
                <h3>Job / Period</h3>
                <p class="small">${escapeHtml(jobLabel)}</p>
                <p class="small muted">Period: ${escapeHtml(formatDateRangeLabel(invoice.period_start, invoice.period_end, organizationDateLocale))}</p>
              </div>
            </section>
            <h2>Work Particulars</h2>
            <table><thead><tr><th>Date / Period</th><th>Job Title</th><th>Ref</th><th>Qty / Hours</th><th>Description</th><th class="right">Amount</th></tr></thead><tbody>${workRows}</tbody></table>
            <section class="summary-row">
              ${renderCompanySealHtml(companyProfile.name, companyProfile.branchName)}
              <div>
                <h2>Totals</h2>
                <table class="totals"><tbody>
                  <tr><td>Total earned</td><td class="right">${escapeHtml(formatCurrency(toNumber(invoice.total_earned)))}</td></tr>
                  <tr><td>Paid</td><td class="right">${escapeHtml(formatCurrency(toNumber(invoice.total_paid)))}</td></tr>
                  <tr class="total-due"><td>Pending</td><td class="right">${escapeHtml(formatCurrency(toNumber(invoice.pending_amount)))}</td></tr>
                </tbody></table>
              </div>
            </section>
            <h2>Payments</h2>
            <table><thead><tr><th>Date</th><th>Method</th><th>Reference</th><th class="right">Amount</th></tr></thead><tbody>${paymentRows}</tbody></table>
            <section class="footer">
              <div>${escapeHtml(companyProfile.name)} · ${escapeHtml(companyProfile.address || 'Address not set')}</div>
              <div>${escapeHtml(compactText(companyProfile.email, companyProfile.phone))}</div>
            </section>
          </main>
        </body>
      </html>
    `

    printWindow.document.open()
    printWindow.document.write(html)
    printWindow.document.close()
    printWindow.focus()
    if (options.print) {
      printWindow.print()
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-10rem)] flex-col gap-4 pb-2">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">Worker invoices</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            All worker payout invoices. Use Worker Payouts to capture payments made to workers against these invoices.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild>
            <Link to={withServiceInstanceId('/job/worker/payments', serviceInstanceId)}>
              <Wallet className="mr-2 h-4 w-4" />
              Open Worker Payouts
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Card className="rounded-2xl border-border/70">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <FileText className="h-4 w-4" />
              <p className="text-sm font-medium text-foreground">Invoices</p>
            </div>
            <p className="mt-2 text-2xl font-semibold">{summary?.periods_count ?? totalCount}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-border/70">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <DollarSign className="h-4 w-4" />
              <p className="text-sm font-medium text-foreground">Invoice amount</p>
            </div>
            <p className="mt-2 text-2xl font-semibold">{formatCurrency(toNumber(summary?.total_payout))}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-border/70">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <CreditCard className="h-4 w-4" />
              <p className="text-sm font-medium text-foreground">Paid</p>
            </div>
            <p className="mt-2 text-2xl font-semibold">{formatCurrency(toNumber(summary?.total_paid))}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-border/70">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <p className="text-sm font-medium text-foreground">Pending</p>
            </div>
            <p className="mt-2 text-2xl font-semibold text-amber-700">{formatCurrency(toNumber(summary?.total_pending))}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border-border/70 bg-card shadow-sm">
        <div className="border-b border-border/60 bg-muted/10 p-3 sm:p-4">
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-6">
            <div className="relative xl:col-span-2">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search workers"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value)
                  setPage(1)
                }}
                className="h-9 rounded-lg border-border/70 bg-background pl-9"
              />
            </div>

            <SearchableSelect
              ariaLabel="Filter by worker"
              value={selectedWorkerId}
              onValueChange={(value) => {
                setSelectedWorkerId(value)
                setPage(1)
              }}
              options={workerFilterOptions}
              placeholder="All workers"
              searchPlaceholder="Search workers"
              emptyText="No workers found."
              triggerClassName="h-9 rounded-lg border-border/70 bg-background"
            />

            <Select value={paymentStatus} onValueChange={(value) => {
              setPaymentStatus(value)
              setPage(1)
            }}>
              <SelectTrigger className="h-9 rounded-lg border-border/70 bg-background">
                <Filter className="mr-2 h-3.5 w-3.5" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PAYMENT_STATUS_OPTIONS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              aria-label="Filter from date"
              value={dateFrom}
              onChange={(event) => {
                setDateFrom(event.target.value)
                setPage(1)
              }}
              className="h-9 rounded-lg border-border/70 bg-background"
            />
            <Input
              type="date"
              aria-label="Filter to date"
              value={dateTo}
              onChange={(event) => {
                setDateTo(event.target.value)
                setPage(1)
              }}
              className="h-9 rounded-lg border-border/70 bg-background"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {invoicesQuery.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 8 }).map((_, index) => (
                <Skeleton key={index} className="h-12 w-full rounded-lg" />
              ))}
            </div>
          ) : invoicesQuery.isError ? (
            <div className="flex h-64 items-center justify-center text-sm text-destructive">
              Failed to load worker invoices.
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/80">
                <TableRow className="hover:bg-muted/95">
                  <TableHead className="h-10 pl-4 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Invoice</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Worker</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Job / Period</TableHead>
                  <TableHead className="text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Invoice Amount</TableHead>
                  <TableHead className="text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Paid</TableHead>
                  <TableHead className="text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Pending</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Latest Payment</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Status</TableHead>
                  <TableHead className="pr-4 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length > 0 ? (
                  rows.map((invoice) => {
                    const payments = getInvoicePayments(invoice)
                    const latestPayment = payments[0]
                    const invoiceRef = getInvoiceDisplayRef(invoice)
                    const workerName = invoice.worker_name || `Worker #${invoice.worker_id ?? '-'}`
                    const payoutParams = new URLSearchParams()
                    if (invoice.worker_id) {
                      payoutParams.set('worker_id', String(invoice.worker_id))
                    }
                    if (invoice.id) {
                      payoutParams.set('invoice_id', String(invoice.id))
                    }
                    if (invoice.job_id) {
                      payoutParams.set('job_id', String(invoice.job_id))
                    }
                    const payoutPath = withServiceInstanceId(
                      `/job/worker/payments?${payoutParams.toString()}`,
                      serviceInstanceId,
                    )
                    const recordPaymentParams = new URLSearchParams(payoutParams)
                    recordPaymentParams.set('action', 'record')
                    const recordPaymentPath = withServiceInstanceId(
                      `/job/worker/payments?${recordPaymentParams.toString()}`,
                      serviceInstanceId,
                    )

                    return (
                      <TableRow key={String(invoice.id)} className="border-border/60 hover:bg-muted/20">
                        <TableCell className="pl-4 py-2.5">
                          <button
                            type="button"
                            className="font-semibold text-primary underline-offset-4 hover:underline"
                            onClick={() => openWorkerInvoice(invoice)}
                          >
                            #{invoiceRef}
                          </button>
                        </TableCell>
                        <TableCell className="py-2.5">
                          {invoice.worker_id ? (
                            <Link
                              to={withServiceInstanceId(`/workers/${invoice.worker_id}`, serviceInstanceId)}
                              className="font-medium text-foreground hover:text-primary"
                            >
                              {workerName}
                            </Link>
                          ) : (
                            <span className="font-medium text-foreground">{workerName}</span>
                          )}
                          {invoice.worker_user_id ? (
                            <p className="text-xs text-muted-foreground">User ID: {invoice.worker_user_id}</p>
                          ) : null}
                        </TableCell>
                        <TableCell className="py-2.5 text-sm">
                          {invoice.job_id ? (
                            <Link
                              to={withServiceInstanceId(`/job/${invoice.job_id}`, serviceInstanceId)}
                              className="font-medium text-foreground hover:text-primary"
                            >
                              {invoice.job_ref ? `Job #${invoice.job_ref}` : invoice.job_title || `Job #${invoice.job_id}`}
                            </Link>
                          ) : (
                            <span className="text-muted-foreground">No linked job</span>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {formatDateRangeLabel(invoice.period_start, invoice.period_end, organizationDateLocale)}
                          </p>
                        </TableCell>
                        <TableCell className="py-2.5 text-right font-medium tabular-nums">
                          {formatCurrency(toNumber(invoice.total_earned))}
                        </TableCell>
                        <TableCell className="py-2.5 text-right tabular-nums text-muted-foreground">
                          {formatCurrency(toNumber(invoice.total_paid))}
                        </TableCell>
                        <TableCell className={cn(
                          'py-2.5 text-right font-semibold tabular-nums',
                          toNumber(invoice.pending_amount) > 0 ? 'text-amber-700' : 'text-foreground',
                        )}>
                          {formatCurrency(toNumber(invoice.pending_amount))}
                        </TableCell>
                        <TableCell className="py-2.5 text-sm">
                          {latestPayment ? (
                            <div>
                              <p className="font-medium text-foreground">
                                {formatCurrency(toNumber(latestPayment.amount))}
                                <span className="ml-1 text-xs font-normal text-muted-foreground">
                                  {latestPayment.payment_method_label || latestPayment.payment_method || '-'}
                                </span>
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {formatDateTimeLabel(latestPayment.paid_on, organizationDateLocale, workerInvoiceTimeZone)}
                                {payments.length > 1 ? ` · ${payments.length} payments` : ''}
                              </p>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">No payment yet</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2.5">{renderStatusBadge(invoice.status)}</TableCell>
                        <TableCell className="pr-4 py-2.5 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg">
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Open invoice actions</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              <DropdownMenuItem onSelect={() => openWorkerInvoice(invoice)}>
                                <Eye className="mr-2 h-4 w-4" />
                                View invoice
                              </DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => openWorkerInvoice(invoice, { print: true })}>
                                <Printer className="mr-2 h-4 w-4" />
                                Print
                              </DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => resendInvoiceMutation.mutate(invoice.id)}>
                                <FileText className="mr-2 h-4 w-4" />
                                {resendInvoiceMutation.isPending ? 'Resending…' : 'Resend invoice'}
                              </DropdownMenuItem>
                              {invoice.worker_id ? (
                                <DropdownMenuItem asChild>
                                  <Link to={payoutPath}>
                                    <UserRound className="mr-2 h-4 w-4" />
                                    Payouts
                                  </Link>
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem disabled>
                                  <UserRound className="mr-2 h-4 w-4" />
                                  Payouts
                                </DropdownMenuItem>
                              )}
                              {toNumber(invoice.pending_amount) > 0 ? (
                                <DropdownMenuItem asChild>
                                  <Link to={recordPaymentPath}>Record payment</Link>
                                </DropdownMenuItem>
                              ) : null}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    )
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} className="h-40">
                      <div className="flex flex-col items-center justify-center gap-2 text-center text-muted-foreground">
                        <FileText className="h-9 w-9 opacity-50" />
                        <p className="font-medium text-foreground">No worker invoices found</p>
                        <p className="text-sm">Completed assignment payout invoices will appear here.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {!invoicesQuery.isLoading && totalCount > 0 ? (
          <div className="border-t border-border/60 bg-muted/10 px-3 py-2 sm:px-4">
            <DataTablePagination
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size)
                setPage(1)
              }}
            />
          </div>
        ) : null}
      </Card>
    </div>
  )
}
